import { useMemo, useState, useCallback } from "react";
//formik
import { Formik, Form, FormikProps, FormikHelpers } from "formik";
import {
  FormikTextField,
  FormikSelectField,
  FormikDateTimeField,
} from "src/components/FormikComponents/FormikWrappers";
import { SwitchField } from "src/components/FormikComponents/SwitchField";
import { TextField } from "src/components/FormikComponents/TextField";
//material UI
import { Button, Grid, Typography, useTheme, Card, CardContent, CardActions, Theme, alpha } from "@mui/material";
import {
  IAircraft,
  IFlightAircraft,
  IFullAircraftConfiguration,
  IFlightType,
  IFlightFlag,
  INewFlight,
  PolicyResource,
} from "@vmosolutions/oc-interfaces";
import { EventTypes } from "../FlightBuilder";
import classNames from "classnames";
import Addbox from "@mui/icons-material/AddBox";
import Remove from "@mui/icons-material/Remove";
import moment from "moment-timezone";
import { isEqual } from "lodash";
// Validation Imports
import { validateDateTimeFormat } from "src/utils/index";

// API calls
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IStructuresState, IAirportOption } from "src/redux/reducers/StructuresReducer";
import { formatIdentFromAirport, updateAirportOptions } from "src/utils/airportHelper";
import { v1 } from "uuid";
import { milli2DaysHrsMin, isEpochDate } from "src/utils/index";

interface ICreateFormFormikProps {
  mosaicId: string;
  initialFlightValues: Partial<INewFlight>;
  onSubmit: (flightValues: INewFlight) => void;
  eventType?: EventTypes;
  selectedRowIndex?: number;
  // saveSelectedRow?: () => void;
  removeSelectedRow?: () => void;
  displayAsUTC: boolean;
  setDisplayAsUTC: (state: boolean) => void;
}

export const defaultFlightValues: INewFlight = {
  _id: undefined,
  flightNumber: undefined,
  aircraft: undefined,
  origin: undefined,
  destination: undefined,
  scheduledOut: undefined,
  scheduledIn: undefined,
  customer: undefined,
  flightType: undefined,
  aircraftConfiguration: undefined,
  note: undefined,
  comment: undefined,
  flags: [],
};

export const getInitialValues = (defaultFlightType?: IFlightType): INewFlight => {
  return { ...defaultFlightValues, _id: `temp-${v1()}`, flightType: defaultFlightType ?? undefined };
};

type NewFlightForm = FormikProps<INewFlight>;

/**
 * Function which renders the FlightFlag (saved as a string), into it's SVG Element
 * @param svgString - SVG string saved in the data.
 * @returns - SVG Element rendered on screen.
 */
export const renderFlagIcon = (svgString: string): JSX.Element => {
  if (svgString) {
    return <div dangerouslySetInnerHTML={{ __html: svgString }} />;
  }
  return null;
};

/**
 * Return block time in d+HH:MM format.
 * @param scheduledOut - epoch
 * @param scheduledIn - epoch
 * @returns string
 */
export const getBlockDurationString = (
  scheduledOut: number | string,
  scheduledIn: number | string,
  isUTC = true,
  originTZ?: string,
  destinationTZ?: string
): string => {
  if (scheduledIn && scheduledOut) {
    // Epoch values are used when editing normal flights.
    if (isEpochDate(scheduledIn) && isEpochDate(scheduledOut)) {
      return milli2DaysHrsMin((scheduledIn as number) - (scheduledOut as number), true);
    } else if (
      typeof scheduledIn === "string" &&
      scheduledIn.search(":") > -1 &&
      typeof scheduledOut === "string" &&
      scheduledOut.search(":") > -1
    ) {
      //if recurring and there are no destination/origin - return HH:mm
      if ((!destinationTZ || !originTZ) && !isUTC) {
        return "Timezone Info Needed"; //"d+HH:mm";
      }
      // Recurring flights used Hrs:mm strings so use moment to calculate diff.
      const startTime = isUTC
        ? moment(scheduledOut, "HH:mm")
        : moment.tz(moment(scheduledOut, "HH:mm").format("YYYY-MM-DD HH:mm:ss"), originTZ);
      const endTime = isUTC
        ? moment(scheduledIn, "HH:mm")
        : moment.tz(moment(scheduledIn, "HH:mm").format("YYYY-MM-DD HH:mm:ss"), destinationTZ);
      // If arrival hrs are smaller than departure, assume arrival is next day (add 24 hours).
      if (endTime.hours() < startTime.hours()) {
        endTime.add(24, "hours");
      }
      // calculate total duration
      const duration = moment.duration(endTime.diff(startTime));
      return milli2DaysHrsMin(duration.asMilliseconds(), true);
    }
  }
  return "d+HH:mm";
};

const GridContainerStyles = (theme: Theme): any => ({
  width: "100%",
  height: "100%",
  display: "flex",
  alignContent: "flex-start",
  position: "relative",
  padding: "10px",
  flexGrow: 1,
  "& .MuiGrid-item": { width: 363 },
  "& .dialog_spinner_progress": { margin: "-10px" },
  "& .field-group": {
    flexDirection: "row",
    width: 380,
  },
  "& .formik_field": {
    width: 363,
    "&.note, &.comment": { width: 736 },
  },
  "& p.required_fields_label, & .crewslot_fields_label": {
    margin: 0,
    fontWeight: 300,
    fontSize: 10,
    textTransform: "uppercase",
    color: theme.custom.forms.labels,
  },
  "& .required_fields, & .crewslot_fields": {
    backgroundColor: alpha(theme.custom.forms.labels, 0.2),
    margin: "2px 0px",
  },
  "& .flight_number": {},
  "& .day_offset, .block_duration": {
    width: "auto",
    "& .input_control": {
      "& label": { width: 80 },
      "& div.input-base": {
        maxWidth: 100,
      },
    },
  },
  "& .oc_datetime": {
    // used in flight
    alignItems: "flex-start",
  },
  "& .scheduled_out, & .scheduled_in": {
    // used in recurring flight
    width: "auto",
    alignItems: "flex-start",
    "& .input_control": {
      "& label": { width: 80 },
      "& .time-input": { width: 70 },
    },
  },
  "& .position_count": {
    width: "auto",
    "& label": { width: "auto" },
    "& .input_control": {
      "& div.input-base": {
        maxWidth: 50,
      },
    },
  },
});

const FormActionsStyles = (theme: Theme): any => ({
  "& .form_button": {
    width: "auto",
    height: 30,
    fontSize: 11,
    lineHeight: "12px",
    padding: "2px 8px",
    margin: "0 2px",
  },
});

////////////////////////////
// FORM Flight Validation //
////////////////////////////
const validateFlight = (values: INewFlight): any => {
  const {
    flightNumber,
    origin,
    destination,
    scheduledOut,
    scheduledIn,
    flightType,
    note,
    aircraft,
    aircraftConfiguration,
  } = values;

  // nested values have to be added here as empty objects.
  const errors: any = {};
  // Only validate if the form is dirty (has changes)

  // const { initialValues } = props;
  // Check if fields are dirty
  // const flightNumberDirty = flightNumber !== initialValues.flightNumber;
  // const originDirty = origin !== initialValues.origin;
  // const destinationDirty = destination !== initialValues.destination;
  // const scheduledOutDirty = scheduledOut !== initialValues.scheduledOut;
  // const scheduledInDirty = scheduledIn !== initialValues.scheduledIn;
  // const flightTypeDirty = !_.isEqual(flightType,initialValues.flightType);

  if (!flightNumber && flightNumber !== 0) {
    errors.flightNumber = "Requires a flight number.";
  }
  if (flightNumber && flightNumber.toString().length > 5) {
    errors.flightNumber = "Flight number must be less than 5 digits.";
  }
  if (!origin) {
    errors.origin = "Requires origin terminal.";
  }
  if (!destination) {
    errors.destination = "Requires destination terminal.";
  }
  if (!scheduledOut) {
    errors.scheduledOut = "Requires scheduled departure date/time.";
  }
  if (!scheduledIn) {
    errors.scheduledIn = "Requires scheduled arrival date/time.";
  }
  if (!flightType) {
    errors.flightType = "Requires an assigned flight type.";
  }

  // Validate Date/Time values.
  const dateTimeArry = ["scheduledOut", "scheduledIn"];
  for (const unTypedField of dateTimeArry) {
    const field = unTypedField as keyof typeof values;
    // Display any inline errors triggered by the component when validating the date and time inputs.
    if (values[field] && !!validateDateTimeFormat(values[field]).errorString) {
      errors[field] = validateDateTimeFormat(values[field]).errorString;
    }
  }

  // Validate small note size
  if (!!note && note.length > 100) {
    errors.note = "Small note cannot exceed 100 characters.";
  }

  // Check if scheduled arrival is after departure.
  if (!errors.scheduledOut && !errors.scheduledIn && !!scheduledIn && !!scheduledOut) {
    if (new Date(scheduledIn) <= new Date(scheduledOut)) {
      errors.scheduledOut = "Flight must depart before its arrival.";
      errors.scheduledIn = "Flight must arrive after its departure.";
    }
  }

  // Check that the aircraft has a configuration
  if (aircraft && !aircraftConfiguration) {
    errors.aircraftConfiguration = "Selected aircraft is missing aircraft configuration.";
  }
  return errors;
};

/**
 * Base Flight Form
 * @param props -
 */
export const CreateFlightFormik = (props: ICreateFormFormikProps): JSX.Element => {
  const {
    mosaicId,
    initialFlightValues,
    onSubmit,
    eventType,
    selectedRowIndex,
    removeSelectedRow,
    displayAsUTC,
    setDisplayAsUTC,
  } = props;

  const mosaicPane = useMemo(() => document.getElementById(`${mosaicId}-panel`), [mosaicId]);

  const theme = useTheme();
  const dispatch = useDispatch();

  // Retrieve Field Permission access check.
  const hasPermissions = useSelector((state: IRootState) => state.authentication.hasPermissions);

  // Structure dependencies
  const structures: IStructuresState = useSelector((state: IRootState) => state.structures);
  const { airportOptions, customerStructure } = structures;
  const { flightTypes, customers, flightFlags } = customerStructure;
  const flightEventTypes = useMemo(() => {
    return flightTypes?.length ? flightTypes.filter((type: IFlightType) => type.isFlight) : [];
  }, [flightTypes]);
  const aircraftOptions = customerStructure?.aircraft;

  // const [displayAsUTC, setDisplayAsUTC] = useState<boolean>(true);

  const [aircraftConfigOptions, setAircraftConfigOptions] = useState<IFullAircraftConfiguration[]>([]);
  const getAircraftConfigurations = useCallback(async (aicraftId: string): Promise<IFullAircraftConfiguration[]> => {
    const response = await AmplifyAPIWrapper.Get<IFullAircraftConfiguration[]>(
      `/aircraftConfiguration?aircraftId=${aicraftId}`
    );
    return Array.isArray(response) ? response : [];
  }, []);

  // Assign default configuration based on selected aircraft (only called onChange);
  const assignDefaultConfiguration = useCallback(
    async (newAC: IAircraft, flightForm: NewFlightForm): Promise<void> => {
      const aircraftConfigOptions = await getAircraftConfigurations(newAC?._id); // Get configuration options for this aircraft.
      setAircraftConfigOptions(aircraftConfigOptions);
      const { setFieldValue, setFieldTouched } = flightForm;
      const ACConfig =
        newAC && aircraftOptions?.length > 0
          ? aircraftOptions.find((full) => full._id === newAC._id)?.defaultConfig
          : null;
      setFieldValue("aircraftConfiguration", ACConfig);
      setFieldTouched("aircraftConfiguration", true);
    },
    [aircraftOptions, getAircraftConfigurations]
  );

  /** PASS Flight values up to flight builder component */
  const handleSubmit = useCallback(
    (values: INewFlight, helpers: FormikHelpers<INewFlight>): void => {
      onSubmit(values);
    },
    [onSubmit]
  );

  const initialFormValues = { ...defaultFlightValues, ...initialFlightValues };
  // console.log("initialFormValues", initialFormValues);

  return (
    <Formik
      initialValues={initialFormValues}
      enableReinitialize={true}
      onSubmit={handleSubmit}
      // validationSchema={FlightTypeSchema} // Look into replacing validation with Yup.
      validate={validateFlight}
    >
      {(flightForm: NewFlightForm) => {
        const { values, dirty, isValid, isSubmitting, resetForm } = flightForm;
        const formIsDefault = isEqual({ ...defaultFlightValues, _id: undefined }, { ...values, _id: undefined });
        const blockDuration = getBlockDurationString(values?.scheduledOut, values?.scheduledIn);

        return (
          <Form className={"form_styles"}>
            {/* <FormikFormIsDirtyEffect updateParent={updateTabState} formDirtyState={dirty} /> */}
            <Card classes={{ root: classNames("flights_form_card") }}>
              <CardContent className="card_content" style={{ padding: 10 }}>
                <Grid container direction="column">
                  <Grid container className={"form_body"} sx={{ ...GridContainerStyles(theme), padding: 0 }}>
                    <Typography className="required_fields_label">required fields</Typography>

                    <Grid container direction="row" className="required_fields">
                      <FormikTextField
                        // className="flight_number"
                        label="Flight #"
                        name="flightNumber"
                        disabled={!hasPermissions(PolicyResource.Flight, "flightNumber")}
                      />

                      <FormikSelectField
                        name="flightType"
                        label="Flight Type"
                        isMulti={false}
                        options={flightEventTypes.sort((a, b) => {
                          return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
                        })}
                        getOptionLabel={(type: IFlightType) => `${type.title} - ${type.description}`}
                        getOptionValue={(flightType: IFlightType) => flightType._id}
                        // placeholder="Select FlightType"
                        isSearchable={true}
                        menuPortalTarget={mosaicPane}
                        menuPosition={"fixed"}
                        disabled={!hasPermissions(PolicyResource.Flight, "flightType")}
                      />

                      <FormikSelectField
                        label="Origin"
                        name="origin"
                        format={(ident: string) => formatIdentFromAirport(ident, Object.values(airportOptions))}
                        parse={(airport: IAirportOption) => airport?.ident}
                        onInputChange={(ident: string) => {
                          updateAirportOptions(ident, airportOptions, dispatch);
                        }}
                        options={Object.values(airportOptions)}
                        getOptionLabel={(airport: IAirportOption) =>
                          `${airport.ident}${airport?.iata ? `/${airport.iata}` : ""} - ${airport.name}`
                        }
                        getOptionValue={(airport: IAirportOption) => airport.ident}
                        isSearchable={true}
                        menuPortalTarget={mosaicPane}
                        menuPosition={"fixed"}
                        openMenuOnFocus={false}
                        isClearable={false}
                        noOptionsMessage={() => "Search airport name or ICAO..."}
                        disabled={!hasPermissions(PolicyResource.Flight, "origin")}
                      />

                      <FormikSelectField
                        label="Destination"
                        name="destination"
                        format={(ident: string) => formatIdentFromAirport(ident, Object.values(airportOptions))}
                        parse={(airport: IAirportOption) => airport?.ident}
                        onInputChange={(ident: string) => {
                          updateAirportOptions(ident, airportOptions, dispatch);
                        }}
                        options={Object.values(airportOptions)}
                        getOptionLabel={(airport: IAirportOption) =>
                          `${airport.ident}${airport?.iata ? `/${airport.iata}` : ""} - ${airport.name}`
                        }
                        getOptionValue={(airport: IAirportOption) => airport.ident}
                        isSearchable={true}
                        menuPortalTarget={mosaicPane}
                        menuPosition={"fixed"}
                        openMenuOnFocus={false}
                        isClearable={false}
                        disabled={!hasPermissions(PolicyResource.Flight, "destination")}
                      />

                      <Grid item style={{ padding: "4px 0px", width: "100%" }}>
                        <SwitchField
                          className={`time_mode_selector ${!displayAsUTC && "active"}`}
                          name={"editflight_time_mode_selector"}
                          value={displayAsUTC}
                          onChange={() => setDisplayAsUTC(!displayAsUTC)}
                          switchlabels={["UTC", "LOCAL"]}
                          switchtooltips={["Times as UTC", "Times as local"]}
                          label="Display Date/Times in"
                          disabled={!values?.destination || !values?.origin}
                          allowNull={false}
                        />
                      </Grid>

                      <FormikDateTimeField
                        // className="field datetimefield blocktimes"
                        label="Sched Out"
                        name="scheduledOut"
                        showAirportTime={values?.origin}
                        localizeDisplay={!displayAsUTC}
                        hideLocalizeBtn={true}
                        isEpoch={eventType === "RecurrentFlight" ? false : true}
                        disabled={!hasPermissions(PolicyResource.Flight, "scheduledOut")}
                      />

                      <FormikDateTimeField
                        // className="field datetimefield blocktimes"
                        label="Sched In"
                        name="scheduledIn"
                        showAirportTime={values?.destination}
                        localizeDisplay={!displayAsUTC}
                        hideLocalizeBtn={true}
                        isEpoch={eventType === "RecurrentFlight" ? false : true}
                        disabled={!hasPermissions(PolicyResource.Flight, "scheduledIn")}
                      />

                      <TextField
                        className="block_duration"
                        label="Block Duration"
                        value={blockDuration}
                        disabled={true}
                      />
                    </Grid>

                    <Grid container direction="row" className="optional_fields">
                      <FormikSelectField
                        name="aircraft"
                        label="Aircraft"
                        isMulti={false}
                        options={aircraftOptions}
                        onChange={(ac) => assignDefaultConfiguration(ac, flightForm)}
                        getOptionLabel={(aircraft: IFlightAircraft) =>
                          `${aircraft.registration} (${aircraft.aircraftType && aircraft.aircraftType.title})`
                        }
                        getOptionValue={(aircraft: IFlightAircraft) => aircraft?._id}
                        // placeholder="Aircraft"
                        menuPortalTarget={mosaicPane}
                        menuPosition={"fixed"}
                        isSearchable={true}
                        isClearable={true}
                        disabled={!hasPermissions(PolicyResource.Flight, "aircraft")}
                      />

                      <FormikSelectField
                        name="aircraftConfiguration"
                        label="AC Config"
                        isMulti={false}
                        options={aircraftConfigOptions}
                        getOptionLabel={(option: any) => option.title}
                        getOptionValue={(option: any) => option._id}
                        // placeholder="Aircraft Configuration"
                        menuPortalTarget={mosaicPane}
                        menuPosition={"fixed"}
                        isSearchable={true}
                        isClearable={true}
                        disabled={!hasPermissions(PolicyResource.Flight, "aircraftConfiguration")}
                      />

                      <FormikSelectField
                        name="customer"
                        label="Customer"
                        isMulti={false}
                        options={customers.sort((a, b) => {
                          return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
                        })}
                        getOptionLabel={(option: any) => option.name}
                        getOptionValue={(option: any) => option._id}
                        // placeholder="Aircraft Configuration"
                        menuPortalTarget={mosaicPane}
                        menuPosition={"fixed"}
                        isSearchable={true}
                        isClearable={true}
                        disabled={!hasPermissions(PolicyResource.Flight, "customer")}
                      />

                      <FormikSelectField
                        name="flags"
                        label="Flags"
                        isMulti={true}
                        options={flightFlags.sort((a, b) => {
                          return a.description.toLowerCase().localeCompare(b.description.toLowerCase());
                        })}
                        parse={(option: any) => option ?? []}
                        getOptionLabel={(option: IFlightFlag) => {
                          return `${option.description} [ ${option.title} ]`;
                        }}
                        formatOptionLabel={(option: IFlightFlag) => {
                          return (
                            <Grid container alignItems="center" wrap="nowrap">
                              <Grid item className="flag_icon" style={{ width: "auto" }}>
                                {renderFlagIcon(option?.icon)}
                              </Grid>
                              <Grid
                                item
                                style={{ marginLeft: 5, width: "auto" }}
                              >{`${option.description} [ ${option.title} ]`}</Grid>
                            </Grid>
                          );
                        }}
                        getOptionValue={(option: IFlightFlag) => option._id}
                        // placeholder="Aircraft Configuration"
                        menuPortalTarget={mosaicPane}
                        menuPosition={"fixed"}
                        isSearchable={true}
                        isClearable={true}
                        disabled={!hasPermissions(PolicyResource.Flight, "flags")}
                      />

                      <FormikTextField
                        className="formik_field textfield multiline_long note"
                        label="Small Note"
                        name="note"
                        multiline={true}
                        minRows={2}
                        maxRows={5}
                        disabled={!hasPermissions(PolicyResource.Flight, "note")}
                      />

                      <FormikTextField
                        className="formik_field textfield multiline_long comment"
                        label="Comment"
                        name="comment"
                        multiline={true}
                        minRows={2}
                        maxRows={5}
                        disabled={!hasPermissions(PolicyResource.Flight, "comment")}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <CardActions sx={FormActionsStyles(theme)}>
                  <Button
                    id={`${mosaicId}_clearField_btn`}
                    className="form_button"
                    variant="contained"
                    color="secondary"
                    disabled={formIsDefault}
                    type="button"
                    onClick={() => {
                      // Reset form with default flight values.
                      resetForm({ values: defaultFlightValues });
                    }}
                  >
                    Clear Fields
                  </Button>
                  <Button
                    id={`${mosaicId}_resetForm_btn`}
                    className="form_button"
                    variant="contained"
                    color="secondary"
                    disabled={(!dirty && isValid) || isSubmitting}
                    type="reset"
                  >
                    Reset Form
                  </Button>

                  {selectedRowIndex === null || selectedRowIndex === undefined ? (
                    <Button
                      id={`${mosaicId}_addFlight_btn`}
                      className="form_button"
                      variant="contained"
                      color="primary"
                      disabled={!dirty || !isValid || isSubmitting}
                      type="submit"
                    >
                      <Addbox style={{ marginRight: 5 }} />
                      Add To Build Queue
                    </Button>
                  ) : (
                    <>
                      <Button
                        id={`${mosaicId}_saveChanges_btn`}
                        className="form_button"
                        variant="contained"
                        color="primary"
                        disabled={!dirty || !isValid || isSubmitting}
                        type="submit"
                      >
                        Save changes
                      </Button>
                      <Button
                        id={`${mosaicId}_removeFlight_btn`}
                        className="form_button"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                        type="button"
                        onClick={removeSelectedRow}
                      >
                        <Remove style={{ marginRight: 5 }} />
                        Remove Flight
                      </Button>
                    </>
                  )}
                </CardActions>
              </CardContent>
            </Card>
          </Form>
        );
      }}
    </Formik>
  );
};
